import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckSquare, FileText, MessageSquare, Sparkles } from 'lucide-react'
import { HighlightedText } from './HighlightedText'
import { SEARCH_GROUP_CLASS, SEARCH_ROW_CLASS } from './searchStyles'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { useComments } from '@/lib/db/rxdb/hooks/entities/comments'
import { usePageContents } from '@/lib/db/rxdb/hooks/entities/page-content'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import AuthAxios from '@/lib/api/axios/AuthAxios'
import { ENDPOINT_BASE } from '@/config/endpoints'

const MAX_HITS = 20

type SmartSearchHit = {
  sourceType: 'Document' | 'WorkItem' | 'Comment'
  sourceId: string
  title: string
  chunkIndex: number
  score: number
  snippet: string
}

type SmartSearchResponse = {
  workspaceId: string
  query: string
  results: Array<SmartSearchHit>
}

export interface TextTarget {
  title: string
  href: string
}

interface SearchTextResultsProps {
  query: string
  workspaceSlug: string | null
  aiAvailable: boolean
  /** Where a task or doc lives, or null when it's gone. */
  resolveTask: (taskId: string) => TextTarget | null
  resolvePage: (pageId: string) => TextTarget | null
  onOpen: (href: string) => void
  /** Reports how many rows are showing, for the empty state. */
  onCountChange: (count: number) => void
}

/** A short window of text around the first match. */
export function snippetAround(text: string, query: string, radius = 48): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  const at = flat.toLowerCase().indexOf(query.toLowerCase())
  if (at < 0) return flat.slice(0, radius * 2)
  const start = Math.max(0, at - radius)
  const end = Math.min(flat.length, at + query.length + radius)
  return `${start > 0 ? '…' : ''}${flat.slice(start, end)}${end < flat.length ? '…' : ''}`
}

/**
 * ⌘K's "Search in comments and doc text" (design 29c): plain-text matches in
 * comments and doc bodies on this device, plus AI results when the workspace
 * has AI (the old "Smart" filter lives here now).
 */
export function SearchTextResults({
  query,
  workspaceSlug,
  aiAvailable,
  resolveTask,
  resolvePage,
  onOpen,
  onCountChange,
}: SearchTextResultsProps) {
  const { t } = useTranslation('common')
  const q = query.trim()
  const { comments } = useComments({ isDeleted: false })
  const { contents: pageContents } = usePageContents()

  const commentHits = useMemo(() => {
    if (!q) return []
    const lower = q.toLowerCase()
    const hits: Array<{ id: string; author: string; snippet: string; target: TextTarget }> = []
    for (const comment of comments) {
      if (hits.length >= MAX_HITS) break
      const text = comment.contentPlainText ?? ''
      if (!text.toLowerCase().includes(lower)) continue
      const target =
        comment.entityType === ENTITY_TYPES.WORK_ITEM
          ? resolveTask(comment.entityId)
          : comment.entityType === ENTITY_TYPES.DOCUMENT
            ? resolvePage(comment.entityId)
            : null
      if (!target) continue
      hits.push({
        id: comment.id,
        author: comment.createdByName ?? '',
        snippet: snippetAround(text, q),
        target,
      })
    }
    return hits
  }, [comments, q, resolveTask, resolvePage])

  const docHits = useMemo(() => {
    if (!q) return []
    const lower = q.toLowerCase()
    const hits: Array<{ id: string; snippet: string; target: TextTarget }> = []
    for (const content of pageContents) {
      if (hits.length >= MAX_HITS) break
      const text = content.plainText ?? ''
      if (!text.toLowerCase().includes(lower)) continue
      const target = resolvePage(content.pageId)
      if (!target) continue
      hits.push({ id: content.id, snippet: snippetAround(text, q), target })
    }
    return hits
  }, [pageContents, q, resolvePage])

  // AI search, debounced; cancels in-flight calls when the query changes.
  const [smartResults, setSmartResults] = useState<Array<SmartSearchHit>>([])
  useEffect(() => {
    if (!aiAvailable || !workspaceSlug || !q) {
      setSmartResults([])
      return
    }
    let cancelled = false
    const handle = setTimeout(async () => {
      try {
        const res = await AuthAxios.post<SmartSearchResponse>(
          `${ENDPOINT_BASE}/e/${workspaceSlug}/ai/search`,
          { query: q, topK: 8 },
        )
        if (!cancelled) setSmartResults(res.data.results)
      } catch {
        if (!cancelled) setSmartResults([])
      }
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [aiAvailable, workspaceSlug, q])

  const smartHits = useMemo(
    () =>
      smartResults
        .map((hit) => ({
          hit,
          target:
            hit.sourceType === 'WorkItem'
              ? resolveTask(hit.sourceId)
              : hit.sourceType === 'Document'
                ? resolvePage(hit.sourceId)
                : null,
        }))
        .filter((entry): entry is { hit: SmartSearchHit; target: TextTarget } => entry.target !== null),
    [smartResults, resolveTask, resolvePage],
  )

  const count = commentHits.length + docHits.length + smartHits.length
  useEffect(() => onCountChange(count), [count, onCountChange])

  const rowClass = `${SEARCH_ROW_CLASS} items-start`
  const iconClass = 'mt-0.5 size-4 shrink-0 text-muted-foreground'

  return (
    <>
      {commentHits.length > 0 && (
        <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.groups.comments')}>
          {commentHits.map((hit) => (
            <CommandItem
              key={hit.id}
              value={`comment:${hit.id}`}
              onSelect={() => onOpen(hit.target.href)}
              className={rowClass}
            >
              <MessageSquare className={iconClass} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-foreground">
                  <HighlightedText text={hit.snippet} query={q} />
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {[hit.author, hit.target.title].filter(Boolean).join(' · ')}
                </div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
      {docHits.length > 0 && (
        <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.groups.docText')}>
          {docHits.map((hit) => (
            <CommandItem
              key={hit.id}
              value={`doctext:${hit.id}`}
              onSelect={() => onOpen(hit.target.href)}
              className={rowClass}
            >
              <FileText className={iconClass} />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{hit.target.title}</div>
                <div className="truncate text-xs text-muted-foreground">
                  <HighlightedText text={hit.snippet} query={q} />
                </div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
      {smartHits.length > 0 && (
        <CommandGroup className={SEARCH_GROUP_CLASS} heading={t('search.groups.smart')}>
          {smartHits.map(({ hit, target }) => (
            <CommandItem
              key={`${hit.sourceType}-${hit.sourceId}-${hit.chunkIndex}`}
              value={`smart:${hit.sourceType}:${hit.sourceId}:${hit.chunkIndex}`}
              onSelect={() => onOpen(target.href)}
              className={rowClass}
            >
              {hit.sourceType === 'WorkItem' ? (
                <CheckSquare className={iconClass} />
              ) : (
                <Sparkles className={iconClass} />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{target.title}</div>
                <div className="truncate text-xs text-muted-foreground">{hit.snippet}</div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </>
  )
}
